'use client'

import type { PageHero } from '@/payload-types';
import { CMSLink } from '@/website/components/CMSLink';
import { Container } from '@/website/components/Container';
import { Media } from '@/website/components/Media';
import { Button } from '@/website/components/ui/button';
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '@/website/components/ui/carousel';
import { useWindowSize } from '@/website/hooks/useWindowSize';
import { cn } from '@/website/utils/tailwindUtils';
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

export const HighImpactHero = (props: Extract<PageHero, object>['highImpact']) => {
	const { heading, subheading, cta, slides } = props!;
	const windowSize = useWindowSize();
	const [api, setApi] = useState<CarouselApi>();
	const [currentIndex, setCurrentIndex] = useState(0);
	const [autoPlayStopped, setAutoPlayStopped] = useState(false);

	const headingRef = useRef<HTMLDivElement>(null!);
	const circleRefs = useRef<HTMLDivElement[]>([]);

	const addCircle = (circle: HTMLDivElement | null) => {
		if(circle && !circleRefs.current.includes(circle)) {
			circleRefs.current.push(circle)
		}
	} 

	const onSelect = useCallback(() => {
		if(!api) return;
		setCurrentIndex(api.selectedScrollSnap());
	}, [api, setCurrentIndex])

	useEffect(() => {
		if(!api) return;
		api.on('select', onSelect)
		api.on('autoplay:stop', () => setAutoPlayStopped(true));
		api.on('autoplay:play', () => setAutoPlayStopped(false));
	}, [api]);

	 useEffect(() => {
		if (headingRef.current) {
			const rect = headingRef.current.getBoundingClientRect();
			const centerX = rect.left + rect.width / 2;
			const centerY = rect.top + rect.height / 2;
			const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
			const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
			const docCenterX = centerX + scrollLeft;
			const docCenterY = centerY + scrollTop;
			
			circleRefs.current.forEach(circle => {
				const circleRect = circle.getBoundingClientRect();
				Object.assign(circle.style, {
					left: `${docCenterX - (circleRect.width / 2)}px`,
					top: `${docCenterY - (circleRect.height / 2)}px`,
					opacity: 1
				})
			})
		}
	}, [windowSize.width, windowSize.height]);

	return (
		<section>
			<div className={cn(
				'relative bg-[#EBC396] pt-14 overflow-hidden min-h-[450px]',
				'lg:min-h-[680px] lg:pt-[88px]'
			)}>
				<span
					ref={addCircle}
					className={cn(
						'absolute w-[85vw] aspect-square rounded-full bg-[#E13B38] z-[5] -translate-y-1/6 opacity-0 transition-opacity ease-in-out duration-150',
						'md:w-[50vw] lg:w-[65vw] lg:translate-y-1/12'
					)}
				/>
				<span
					ref={addCircle}
					className={cn(
						'absolute w-[110vw] aspect-square rounded-full bg-[#E35631] z-[4] -translate-y-1/8 opacity-0 transition-opacity ease-in-out duration-300',
						'md:w-[75vw] lg:w-[75vw] lg:translate-y-1/12'
					)}
				/>
				<span
					ref={addCircle}
					className={cn(
						'absolute w-[135vw] aspect-square rounded-full bg-[#EE9735] z-[3] -translate-y-1/10 opacity-0 transition-opacity ease-in-out duration-[600ms]',
						'md:w-[100vw] lg:w-[85vw] lg:translate-y-1/12'
					)}
				/>
				<span
					ref={addCircle}
					className={cn(
						'absolute w-[160vw] aspect-square rounded-full bg-[#EDAC64] z-[2] -translate-y-1/12 opacity-0 transition-opacity ease-in-out duration-[900ms]',
						'md:w-[125vw] lg:w-[95vw] lg:translate-y-1/12'
					)}
				/>
				<Container ref={headingRef} className={cn('text-white text-center relative z-10 max-w-[680px]')}>
					<h1 className={cn('text-[2.125rem] leading-[1.125] text-balance lg:text-[3.75rem]', subheading ? 'mb-2' : 'mb-8')}>{heading}</h1>
					{subheading && <p className={cn('text-lg line-clamp-2 mb-4 lg:mb-8')}>{subheading}</p>}
					<CMSLink {...cta} size={"default"} appearance={"secondary"} />
				</Container>
			</div>
			{slides && (
				<div className={cn(
					'relative -top-[170px] bg-white -mb-[170px]',
					'lg:top-0 lg:mb-0',
					slides.length > 0 ? 'lg:h-[290px] xl:h-[340px]' : 'lg:h-[290px]'
				)}>
					<Carousel
						setApi={setApi}
						className={cn('z-20 lg:-top-[232px] xl:-top-[290px]')}
						opts={{
							active: (slides?.length ?? 0) > 1,
							duration: 50,
							loop: true,
						}}
						plugins={[
							Autoplay({
								delay: 5000,
								stopOnMouseEnter: true,
								stopOnInteraction: false,
							})
						]}
					>
						<CarouselContent>
							{slides.map((slide, index) => 
								<CarouselItem key={index} className={cn('basis-full')}>
									<div className={cn(
										'flex justify-center max-w-[347px] lg:max-w-[944px] xl:max-w-[1200px] mx-auto gap-2.5',
										slide.desktopMedia && 'justify-between'
									)}>
										{slide.desktopMedia && (
											<div className={cn('relative hidden w-[653px] h-[456px] xl:w-[830px] xl:h-[580px] lg:block')}>
												<Media resource={slide.desktopMedia} fill className={cn('object-contain object-center')} />
											</div>
										)}
										<div className={cn('relative w-[347px] h-[580px] lg:w-[272px] lg:h-[456px] xl:w-[347px] xl:h-[580px]')}>
											<Media resource={slide.mobileMedia} fill className={cn('object-contain object-center')} />
										</div>
									</div>
								</CarouselItem>
							)}
						</CarouselContent>
						{slides.length > 1 && (
							<div className={cn('flex justify-center items-center mt-1')}>
								<div className={cn('flex justify-center gap-x-2')}>
									{slides.map((_, index) => 
										<button
											key={index}
											onClick={() => { api?.scrollTo(index); api?.plugins().autoplay.reset(); }}
											className={cn(
												'relative',
												'w-3 h-1 bg-[#E3E4E4] rounded-full transition-all duration-300',
												'before:block before:absolute before:top-0 before:left-0 before:w-0 before:h-full before:rounded-full before:bg-[#EC5024]',
												currentIndex === index && 'w-20',
												currentIndex === index && !autoPlayStopped && 'before:animate-carousel-dots before:w-full'
											)}
										>
											<span className={cn('sr-only')}>Scroll to slide {index + 1}</span>
										</button>
									)}
								</div>
								<div className={cn('ml-7')}>
									<Button
										size={'icon'}
										className={cn('bg-transparent text-(--button-primary-background) shadow-none rounded-none hover:shadow-none')}
										onClick={() => { api?.scrollPrev(); api?.plugins().autoplay.reset(); }}
									>
										<ChevronLeft className={cn('size-6')} />
									</Button>
									<Button
										size={'icon'}
										className={cn('ml-(--ds-spacing-xs) bg-transparent text-(--button-primary-background) shadow-none rounded-none hover:shadow-none')}
										onClick={() => { api?.scrollNext(); api?.plugins().autoplay.reset(); }}
									>
										<ChevronRight className={cn('size-6')} />
									</Button>
								</div>
							</div>
						)}
					</Carousel>
				</div>
			)}
		</section>
	)
}
